/****************************************************
 * Module Name:  assignTags.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import logo from '../../images/pansuriya.jpg'
import "./../../style/modalstyle.css";
import CommonSnackbar from "../common/commonSnackBar";

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { LoadingButton } from "@mui/lab";
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

const AssignTag = ({ reassignTags, handleClose, show, handleUpdateTag, newTag, loading }) => {

    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [successSnackbar, setSuccessSnackbar] = useState(false);
    const [rows, setRows] = useState(reassignTags.sort((a, b) => a.stoneid - b.stoneid));
    const ws = new WebSocket(Constants.RFID_API);

    useEffect(() => {
        startScanForCurrentIndex();
    }, [])

    const startScanForCurrentIndex = () => {

        // Open WebSocket connection and start scanning
        ws.onopen = () => {
            ws.send("START_SCAN");
        }

        // Listen for incoming messages from WebSocket
        let currIdx = 0;
        let newRows = rows;
        let lstTags = new Set();
        ws.onmessage = (e) => {
            let message = typeof e.data === 'string' ? e.data : String(e.data);
            if (message.includes("TAG: ")) {
                const parts = message.split("TAG: ");
                if (parts.length === 2) {
                    const remaining = parts[1].trim(); // remove extra whitespace
                    remaining.split(',').forEach(str => lstTags.add(str.trim())); // split and trim each item
                }

                if (lstTags.size <= newRows.length) {
                    // Loop through the records to assign the tags
                    const tagsArray = [...lstTags];
                    newRows = newRows.map((row, index) => {
                        // Ensure the record index is within the tag list AND the record's tag value is empty
                        if (index < lstTags.size && !row.tag) {
                            setTimeout(() => {
                                scrollToElement(newRows[currIdx - 2].stoneid)
                            }, 1000);
                            return { ...row, status: "TAGGED", tag: tagsArray[index] };
                        }
                        return row;
                    });
                }

                // Increase current index to track scrolling position
                currIdx++;
                setRows(newRows);

                // Scroll to the element two rows back (if it exists)
            } else if (message.includes("SCAN_COMPLETED") && currIdx < newRows.length) {
                ws.send("START_SCAN");
            }

            if (lstTags.size === newRows.length) {
                if (ws && ws.readyState === WebSocket.OPEN) {
                    ws.close();
                }
            }
        }
    };

    // Function to scroll to an element with the given stoneid
    const scrollToElement = (stoneid) => {
        let elem = document.getElementById(stoneid);
        if (elem) {
            elem.scrollIntoView();
        }
    };

    const handleData = () => {
        let data = [];
        rows.map(row => data.push({ stoneid: row.stoneid, tag: row.tag }));
        if (newTag) {
            handleUpdateTag({ stones: data });
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.close();
            }
        } else {
            handleUpdateTag(data);
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.close();
            }
        }
    }

    const closeModal = () => {
        handleClose();
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.close();
        }
    }

    return (

        <div>

            {successSnackbar ? (<CommonSnackbar
                show={successSnackbar}
                message="Tags Assigned Successfully"
                variant={"success"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Success"}
                handleClose={() => setSuccessSnackbar(false)}
            />) : null}

            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />

            <Modal backdrop="static" show={show} onHide={closeModal} size="lg" enforceFocus={false}>

                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Assign Tags
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <TableContainer component={Paper} sx={{ width: "100%", height: "50vh" }}>
                        <Table sx={{ minWidth: 650, textAlign: "center" }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Stone ID</TableCell>
                                    <TableCell>Certificate ID</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="center">New Tag</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow
                                        key={row.stoneid}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: row.tag !== null ? "#fef0f4" : null }}
                                    >
                                        <TableCell component="th" scope="row" sx={{ width: '120px' }}>
                                            <div id={row.stoneid}>{row.stoneid}</div>
                                        </TableCell>
                                        <TableCell sx={{ width: '150px' }}>{row.certificateid}</TableCell>
                                        <TableCell sx={{ width: '150px' }} align="center">{row.status}</TableCell>
                                        <TableCell align="center">{row.tag === null ? " - " : row.tag}</TableCell>
                                        <TableCell align="center">
                                            <DeleteForeverIcon
                                                style={{ cursor: "pointer", fontSize: "1.5rem" }}
                                                color={"error"}
                                                onClick={() => setRows(prevRows => prevRows.filter((stone) => stone.stoneid !== row.stoneid))}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Modal.Body>

                <Modal.Footer>
                    <LoadingButton
                        variant="contained"
                        color="success"
                        style={{ marginRight: '20px' }}
                        onClick={handleData}
                        loading={loading}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                    >
                        Submit
                    </LoadingButton>
                    <Button variant="contained" color="error" onClick={closeModal} disabled={loading}>
                        Close
                    </Button>
                </Modal.Footer>

            </Modal>

        </div>
    );
};

export default AssignTag;
